import { Button } from '@/components/ui/button'
import { PenLine, MoreHorizontal, ArrowUpDown, Trash2, Pencil, Loader2, FileInput } from 'lucide-react'
import { formatDate } from '@/lib/format'
import type { QuoteDraftVersion } from './types'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useMemo, useState } from 'react'
import type { QuoteDraftLinePreview } from './types'
import { QuoteLineReorderDialog } from './QuoteLineReorderDialog'
import { DeleteQuoteLineDialog } from './DeleteQuoteLineDialog'
import { isImportedLine, originSummary } from './imported-line'
import { cn } from '@/lib/utils'
import { SupplyModeChip } from '@/components/features/supply/SupplyModeChip'

interface ActiveDraftCardProps {
  draft: QuoteDraftVersion
  onAddLine: () => void
  onGenerateVersion: () => void | Promise<void>
  onAddDiscount: () => void
  onAddNote: () => void
  onEditWorkaroundNote: () => void
  onEditLine: (lineId: string) => void
  /** Opens « Origine Winner » of an imported line; the menu entry is shown only when given. */
  onShowLineOrigin?: (lineId: string) => void
  onMoveLine: (lineId: string, newDisplayOrder: number) => void | Promise<void>
  onDeleteLine: (lineId: string) => void | Promise<void>
  /** SOLY-IMPORT-003 S11 — opens a line's supply panel; the Appro column shows only when given. */
  onEditSupply?: (lineId: string) => void
  /** Supplier names by id, for the Appro column. */
  supplierNames?: ReadonlyMap<string, string>
  onEditBaseFinishes?: () => void
  showBaseFinishesActions?: boolean
  showFooterActions?: boolean
}

export function ActiveDraftCard({
  draft,
  onAddLine,
  onGenerateVersion,
  onAddDiscount,
  onAddNote,
  onEditWorkaroundNote: _onEditWorkaroundNote,
  onEditLine,
  onShowLineOrigin,
  onMoveLine,
  onDeleteLine,
  onEditSupply,
  supplierNames,
  onEditBaseFinishes,
  showBaseFinishesActions = true,
  showFooterActions = true,
}: ActiveDraftCardProps) {
  const previewLines = useMemo(() => (draft.linesPreview || []), [draft.linesPreview])
  const gridColumns = onEditSupply
    ? 'grid-cols-[52px_1fr_64px_110px_40px_200px_44px]'
    : 'grid-cols-[52px_1fr_64px_110px_40px_44px]'

  /**
   * A line the total does not count.
   *
   * The domain drops a line from the total when the quantity OR the unit price is missing
   * (`QuoteLine.RecalculateTotals` nulls every total on either), so counting only the
   * priceless ones would make the note itself under-report — the very defect it exists to
   * fix. A blank quantity is reachable: the editor sets it to null on an empty blur and does
   * not gate submit on it.
   *
   * `isIncomplete` is the backend's own gate (ProjectQuoteRepository.cs:114) and is what
   * keeps a note line out: it carries no price by design and nothing counts it. Absent on an
   * older payload, in which case nothing is flagged rather than everything.
   */
  const isUncounted = (line: QuoteDraftLinePreview) =>
    line.isIncomplete === true
    && !(typeof line.unitPrice === 'number' && typeof line.quantity === 'number')

  const uncountedLines = useMemo(() => previewLines.filter(isUncounted), [previewLines])

  const rawWorkaroundNote = draft.workaroundRawNote?.trim() ? draft.workaroundRawNote.trim() : ''

  const hasAnyBaseFinishes = useMemo(() => {
    const bf = draft.baseFinishes
    if (!bf) return false
    return Boolean(bf.facade || bf.carcass || bf.handles || bf.plinth)
  }, [draft.baseFinishes])

  const isBaseFinishesDefined = useMemo(() => {
    const bf = draft.baseFinishes
    if (!bf) return false

    const hasFacade = Boolean(
      bf.facade?.material ||
        bf.facade?.color ||
        bf.facade?.edgeSameAsDoor != null ||
        bf.facade?.edgeColor
    )

    const hasCarcass = Boolean(
      bf.carcass?.finish ||
        bf.carcass?.color ||
        bf.carcass?.edgeSameAsDoor != null ||
        bf.carcass?.edgeColor ||
        bf.carcass?.sideSameAsDoor != null ||
        bf.carcass?.sideFinish
    )

    const hasHandles = Boolean(bf.handles?.model || bf.handles?.color)
    const hasPlinth = Boolean(
      bf.plinth?.materialSameAsDoor != null ||
        bf.plinth?.material ||
        bf.plinth?.colorSameAsDoor != null ||
        bf.plinth?.color ||
        bf.plinth?.height != null
    )

    return hasFacade || hasCarcass || hasHandles || hasPlinth
  }, [draft.baseFinishes])

  const [isReorderOpen, setIsReorderOpen] = useState(false)
  const [reorderLine, setReorderLine] = useState<QuoteDraftLinePreview | null>(null)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [deleteLine, setDeleteLine] = useState<QuoteDraftLinePreview | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)

  return (
    <div className="mt-3 rounded-md border bg-muted/30 px-4 py-3 relative">
      {isGenerating ? (
        <div className="absolute inset-0 z-10 rounded-md bg-background/60 backdrop-blur-[1px] flex items-center justify-center">
          <div className="flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Génération du devis…</span>
          </div>
        </div>
      ) : null}

      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <PenLine className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">En cours</span>              
            </div>
            <p className="text-xs text-muted-foreground">
              Créé le {formatDate(draft.createdAt)}
              {draft.validUntil ? ` • validité ${formatDate(draft.validUntil)}` : ''}
            </p>
          </div>
        </div>
      </div>

      {showBaseFinishesActions && isBaseFinishesDefined ? (
        <div className="mt-3 flex items-center text-xs text-muted-foreground">
          <span>
            💡 Finitions de base
            (<Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs text-muted-foreground"
              onClick={onEditBaseFinishes}
              disabled={!onEditBaseFinishes || isGenerating}
            >
              {' Modifier'}
            </Button>)
          </span>
        </div>
      ) : showBaseFinishesActions ? (
        <div className="mt-3 rounded-md border border-amber-400/60 bg-amber-50 px-3 py-2">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-medium text-amber-900">Finitions de base non définies</div>
              <div className="text-xs text-amber-900/80">
                À définir (façade, caisse, poignées, socle).
              </div>
            </div>
            <Button
              size="sm"
              onClick={onEditBaseFinishes}
              disabled={!onEditBaseFinishes || isGenerating}
              className="flex-shrink-0"
            >
              Définir
            </Button>
          </div>
        </div>
      ) : null}

      {hasAnyBaseFinishes && (
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {draft.baseFinishes?.facade ? (
            <div className="rounded-md border bg-background px-2 py-2">
              <div className="text-[11px] font-medium mb-1">Façade</div>
              <div className="grid grid-cols-[72px_1fr] gap-x-2 gap-y-0.5 text-[11px]">
                <div className="text-muted-foreground">Matériau</div>
                <div className={draft.baseFinishes.facade.material ? '' : 'text-destructive'}>
                  {draft.baseFinishes.facade.material || '—'}
                </div>
                <div className="text-muted-foreground">Coloris</div>
                <div className={draft.baseFinishes.facade.color ? '' : 'text-destructive'}>
                  {draft.baseFinishes.facade.color || '—'}
                </div>
                <div className="text-muted-foreground">Chant</div>
                <div
                  className={
                    draft.baseFinishes.facade.edgeSameAsDoor === true ||
                    (draft.baseFinishes.facade.edgeSameAsDoor === false && draft.baseFinishes.facade.edgeColor)
                      ? ''
                      : 'text-destructive'
                  }
                >
                  {draft.baseFinishes.facade.edgeSameAsDoor === true
                    ? 'Identique façade'
                    : draft.baseFinishes.facade.edgeSameAsDoor === false
                      ? draft.baseFinishes.facade.edgeColor || '—'
                      : '—'}
                </div>
              </div>
            </div>
          ) : null}

          {draft.baseFinishes?.carcass ? (
            <div className="rounded-md border bg-background px-2 py-2">
              <div className="text-[11px] font-medium mb-1">Caisse</div>
              <div className="grid grid-cols-[72px_1fr] gap-x-2 gap-y-0.5 text-[11px]">
                <div className="text-muted-foreground">Finition</div>
                <div className={draft.baseFinishes.carcass.finish ? '' : 'text-destructive'}>
                  {draft.baseFinishes.carcass.finish || '—'}
                </div>
                <div className="text-muted-foreground">Coloris</div>
                <div className={draft.baseFinishes.carcass.color ? '' : 'text-destructive'}>
                  {draft.baseFinishes.carcass.color || '—'}
                </div>
                <div className="text-muted-foreground">Chant</div>
                <div
                  className={
                    draft.baseFinishes.carcass.edgeSameAsDoor === true ||
                    (draft.baseFinishes.carcass.edgeSameAsDoor === false && draft.baseFinishes.carcass.edgeColor)
                      ? ''
                      : 'text-destructive'
                  }
                >
                  {draft.baseFinishes.carcass.edgeSameAsDoor === true
                    ? 'Identique façade'
                    : draft.baseFinishes.carcass.edgeSameAsDoor === false
                      ? draft.baseFinishes.carcass.edgeColor || '—'
                      : '—'}
                </div>
                <div className="text-muted-foreground">Côté</div>
                <div
                  className={
                    draft.baseFinishes.carcass.sideSameAsDoor === true ||
                    (draft.baseFinishes.carcass.sideSameAsDoor === false && draft.baseFinishes.carcass.sideFinish)
                      ? ''
                      : 'text-destructive'
                  }
                >
                  {draft.baseFinishes.carcass.sideSameAsDoor === true
                    ? 'Identique façade'
                    : draft.baseFinishes.carcass.sideSameAsDoor === false
                      ? draft.baseFinishes.carcass.sideFinish || '—'
                      : '—'}
                </div>
              </div>
            </div>
          ) : null}

          {draft.baseFinishes?.handles ? (
            <div className="rounded-md border bg-background px-2 py-2">
              <div className="text-[11px] font-medium mb-1">Poignées</div>
              <div className="grid grid-cols-[72px_1fr] gap-x-2 gap-y-0.5 text-[11px]">
                <div className="text-muted-foreground">Modèle</div>
                <div className={draft.baseFinishes.handles.model ? '' : 'text-destructive'}>
                  {draft.baseFinishes.handles.model || '—'}
                </div>
                <div className="text-muted-foreground">Coloris</div>
                <div className={draft.baseFinishes.handles.color ? '' : 'text-destructive'}>
                  {draft.baseFinishes.handles.color || '—'}
                </div>
              </div>
            </div>
          ) : null}

          {draft.baseFinishes?.plinth ? (
            <div className="rounded-md border bg-background px-2 py-2">
              <div className="text-[11px] font-medium mb-1">Socle</div>
              <div className="grid grid-cols-[72px_1fr] gap-x-2 gap-y-0.5 text-[11px]">
                <div className="text-muted-foreground">Matériau</div>
                <div className={draft.baseFinishes.plinth.material ? '' : 'text-destructive'}>
                  {draft.baseFinishes.plinth.material || '—'}
                </div>
                <div className="text-muted-foreground">Coloris</div>
                <div
                  className={
                    draft.baseFinishes.plinth.colorSameAsDoor === true ||
                    (draft.baseFinishes.plinth.colorSameAsDoor === false && draft.baseFinishes.plinth.color)
                      ? ''
                      : 'text-destructive'
                  }
                >
                  {draft.baseFinishes.plinth.colorSameAsDoor === true
                    ? 'Identique façade'
                    : draft.baseFinishes.plinth.colorSameAsDoor === false
                      ? draft.baseFinishes.plinth.color || '—'
                      : '—'}
                </div>
                <div className="text-muted-foreground">Hauteur</div>
                <div className={draft.baseFinishes.plinth.height != null ? '' : 'text-destructive'}>
                  {draft.baseFinishes.plinth.height != null ? `${draft.baseFinishes.plinth.height} cm` : '—'}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) }

      <div className="mt-3 space-y-2">
        {(draft.linesPreview || []).length === 0 ? (
          <p className="text-sm text-muted-foreground">(vide)</p>
        ) : (
          <div className="rounded-md border bg-background overflow-hidden">
            <div className={cn('grid items-center gap-2 px-3 py-2 text-[11px] text-muted-foreground border-b bg-muted/30', gridColumns)}>
              <div className="text-right">N°</div>
              <div>Désignation</div>
              <div className="text-right">Qté</div>
              <div className="text-right">PU HT</div>
              <div className="text-right">TVA</div>
              {onEditSupply ? <div>Appro</div> : null}
              <div />
            </div>

            {previewLines.map((line) => (
              <div
                key={line.id}
                data-testid="quote-line-row"
                data-line-id={line.id}
                className={cn('grid items-center gap-2 px-3 py-1 border-b last:border-b-0', gridColumns)}
              >
                <div className="text-xs text-muted-foreground text-right tabular-nums">
                  {typeof line.displayOrder === 'number' ? line.displayOrder : '-'}
                </div>

                <div className="min-w-0">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <div className="text-sm font-medium truncate">{line.label}</div>
                    {line.origin?.isModifiedSinceImport ? (
                      <span
                        className="shrink-0 rounded-sm bg-amber-50 px-1.5 py-px text-[10px] font-medium text-amber-800 ring-1 ring-inset ring-amber-200"
                        data-testid="quote-line-modified-badge"
                      >
                        modifiée depuis l’import
                      </span>
                    ) : null}
                  </div>
                  {line.origin && originSummary(line.origin) ? (
                    <div className="text-[11px] text-muted-foreground/80 truncate tabular-nums" data-testid="quote-line-origin-summary">
                      {originSummary(line.origin)}
                    </div>
                  ) : null}
                  {line.description ? (
                    <div className="text-xs text-muted-foreground truncate">{line.description}</div>
                  ) : null}
                </div>

                <div
                  className={
                    isUncounted(line) && typeof line.quantity !== 'number'
                      ? 'text-right text-xs font-medium text-amber-700 tabular-nums'
                      : 'text-right text-xs text-muted-foreground tabular-nums'
                  }
                  data-testid={
                    isUncounted(line) && typeof line.quantity !== 'number'
                      ? 'line-missing-quantity'
                      : undefined
                  }
                >
                  {typeof line.quantity === 'number'
                    ? line.quantity
                    : isUncounted(line)
                      ? 'Qté ?'
                      : '-'}
                </div>

                {/* A priceless line is dropped from the total in silence. A grey "-", the same
                    glyph a legitimately empty cell uses, is not a way of saying so. */}
                <div
                  className={
                    isUncounted(line) && typeof line.unitPrice !== 'number'
                      ? 'text-right text-xs font-medium text-amber-700 tabular-nums'
                      : 'text-right text-xs text-muted-foreground tabular-nums'
                  }
                  data-testid={
                    isUncounted(line) && typeof line.unitPrice !== 'number'
                      ? 'line-missing-price'
                      : undefined
                  }
                >
                  {typeof line.unitPrice === 'number'
                    ? `${line.unitPrice.toFixed(2)} €`
                    : isUncounted(line)
                      ? 'Prix manquant'
                      : '-'}
                </div>

                <div className="text-right text-xs text-muted-foreground tabular-nums">
                  {typeof line.vatPercent === 'number' ? `${line.vatPercent}%` : '-'}
                </div>

                {onEditSupply ? (
                  <div className="min-w-0">
                    {line.supply ? (
                      <SupplyModeChip
                        supply={line.supply}
                        supplierName={line.supply.supplierId ? supplierNames?.get(line.supply.supplierId) : null}
                        onClick={isGenerating ? undefined : () => onEditSupply(line.id)}
                        testId={`quote-line-supply-${line.id}`}
                        showReference={false}
                      />
                    ) : null}
                  </div>
                ) : null}

                <div className="flex justify-end">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        disabled={isGenerating}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                        }}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onSelect={() => onEditLine(line.id)} disabled={isGenerating}>
                        <Pencil className="h-4 w-4" />
                        Modifier
                      </DropdownMenuItem>
                      {onShowLineOrigin && isImportedLine(line) ? (
                        <DropdownMenuItem onSelect={() => onShowLineOrigin(line.id)} data-testid="quote-line-origin-menu-item">
                          <FileInput className="h-4 w-4" />
                          Origine Winner
                        </DropdownMenuItem>
                      ) : null}
                      <DropdownMenuItem
                        disabled={isGenerating}
                        onSelect={() => {
                          setReorderLine(line)
                          setIsReorderOpen(true)
                        }}
                      >
                        <ArrowUpDown className="h-4 w-4" />
                        Changer l’ordre…
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        variant="destructive"
                        disabled={isGenerating}
                        onSelect={() => {
                          setDeleteLine(line)
                          setIsDeleteOpen(true)
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                        Supprimer
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}

            {rawWorkaroundNote ? (
              <div className="px-3 py-2 text-sm whitespace-pre-wrap">{rawWorkaroundNote}</div>
            ) : null}
          </div>
        )}

        <div className="mt-3 border-t pt-2">
          {(() => {
            const totalHT = typeof draft.totals.totalBeforeVat === 'number' ? draft.totals.totalBeforeVat : null
            const totalTTC = typeof draft.totals.totalWithVat === 'number' ? draft.totals.totalWithVat : null
            const vatLines = draft.totals.vatBreakdown ?? []
            // What the total does NOT say on its own: an incomplete line is excluded from it.
            // Measured on staging on 2026-09-21 — five lines on screen, « Total HT 1 290,00 € »
            // counting two. The money rule is right; the screen was silent.
            const uncountedCount = uncountedLines.length
            const fmt = (n: number) => {
              const parts = n.toFixed(2).split('.')
              const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
              return `${intPart},${parts[1]} €`
            }
            return (
              <div className="space-y-1 text-sm">
                {totalHT != null && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total HT</span>
                    <span
                      className="font-medium"
                      data-testid="total-ht"
                      data-amount={totalHT.toFixed(2)}
                    >
                      {fmt(totalHT)}
                    </span>
                  </div>
                )}
                {vatLines.map(({ vatRate, amount }) => (
                  <div key={vatRate} className="flex justify-between text-xs text-muted-foreground">
                    <span>TVA {vatRate}%</span>
                    <span
                      data-testid={`vat-${vatRate}`}
                      data-amount={amount.toFixed(2)}
                    >
                      {fmt(amount)}
                    </span>
                  </div>
                ))}
                {totalTTC != null && (
                  <div className="flex justify-between border-t pt-1 font-semibold">
                    <span>Total TTC</span>
                    <span
                      data-testid="total-ttc"
                      data-amount={totalTTC.toFixed(2)}
                    >
                      {fmt(totalTTC)}
                    </span>
                  </div>
                )}
                {uncountedCount > 0 && (
                  <p className="text-xs text-amber-700" data-testid="totals-priceless-note">
                    {uncountedCount === 1
                      ? '1 ligne incomplète n’est pas comptée dans ce total.'
                      : `${uncountedCount} lignes incomplètes ne sont pas comptées dans ce total.`}
                  </p>
                )}
              </div>
            )
          })()
        }

          {showFooterActions ? (
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 sm:items-start">
              <div className="space-y-2">
                <Button
                  data-testid="quote-generate-version-btn"
                  size="sm"
                  onClick={async () => {
                    setIsGenerating(true)
                    try {
                      await onGenerateVersion()
                    } finally {
                      setIsGenerating(false)
                    }
                  }}
                  disabled={!draft.canGenerateVersion || isGenerating}
                  title={draft.generateDisabledReason || undefined}
                >
                  {isGenerating ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Ouverture…
                    </span>
                  ) : (
                    'Aperçu du devis (PDF)'
                  )}
                </Button>
              </div>

              <div className="flex items-center gap-2 sm:justify-end">
                <Button size="sm" variant="outline" onClick={onAddNote} disabled={isGenerating}>
                  + Note
                </Button>
                <Button size="sm" variant="outline" onClick={onAddDiscount} disabled={isGenerating}>
                  - Remise
                </Button>
                <Button size="sm" variant="outline" onClick={onAddLine} disabled={!draft.canAddLine || isGenerating}>
                  + Ajouter élément
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <QuoteLineReorderDialog
        open={isReorderOpen}
        onOpenChange={(open) => {
          setIsReorderOpen(open)
          if (!open) setReorderLine(null)
        }}
        line={reorderLine}
        totalLines={(draft.linesPreview || []).length}
        onReorder={async (newDisplayOrder) => {
          if (!reorderLine) return
          await onMoveLine(reorderLine.id, newDisplayOrder)
        }}
      />

      <DeleteQuoteLineDialog
        open={isDeleteOpen}
        onOpenChange={(open) => {
          setIsDeleteOpen(open)
          if (!open) setDeleteLine(null)
        }}
        line={deleteLine}
        onConfirm={async () => {
          if (!deleteLine) return
          await onDeleteLine(deleteLine.id)
          setIsDeleteOpen(false)
          setDeleteLine(null)
        }}
      />
    </div>
  )
}
